import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/**
 * "What already exists for this ticket?" — the duplicate-work guard.
 *
 * Shown before anyone authors anything, in both creation flows:
 *   · Explore & Author › Build Xray tests   (before the agent drafts)
 *   · Create tests dialog                    (before the Playwright flow starts)
 *
 * Reads /api/coverage/tickets, which answers two questions per ticket:
 *   1. which Xray Tests are already linked to it, and
 *   2. which of those already have a Cortex/Playwright test case.
 *
 * Nothing here changes data — it exists so the user can see, in a table, exactly
 * what would be repeated. The backend independently skips duplicates; this makes
 * that decision visible instead of silent.
 */

export function coverageSummaryLine(totals) {
  if (!totals) return "";
  const { xray_tests: x = 0, automated: a = 0, other_testcases: o = 0 } = totals;
  if (!x && !o) return "Nothing exists yet — everything you create here will be new.";
  const bits = [];
  if (x) bits.push(`${x} test${x === 1 ? "" : "s"} already exist`);
  if (a) bits.push(`${a} of them already have an Artifact`);
  if (o) bits.push(`${o} other Cortex test case${o === 1 ? "" : "s"} on the ticket`);
  return bits.join(" · ");
}

function Pill({ tone = "slate", children, title }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    green: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    violet: "bg-violet-100 text-violet-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export default function TicketCoveragePanel({
  ticketKeys = [],
  onLoaded,
  className = "",
  title = "What already exists",
  defaultOpen = true,
}) {
  const keys = useMemo(
    () => Array.from(new Set((ticketKeys || []).filter(Boolean))),
    [ticketKeys],
  );
  const keyParam = keys.join(",");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(
    async (refresh = false) => {
      if (!keyParam) {
        setData(null);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${API_BASE_URL}coverage/tickets?keys=${encodeURIComponent(keyParam)}${
            refresh ? "&refresh=true" : ""
          }`,
        );
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          setData(null);
          setError(body?.detail || `HTTP ${res.status}`);
          return;
        }
        setData(body?.data || null);
      } catch (e) {
        setData(null);
        setError(`Could not check existing tests: ${e.message}`);
      } finally {
        setLoading(false);
      }
    },
    [keyParam],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Let the parent react to the roll-up (e.g. enable an "allow duplicates" toggle).
  useEffect(() => {
    if (onLoaded) onLoaded(data?.totals || null, data);
  }, [data, onLoaded]);

  const toggleRow = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  if (!keyParam) return null;

  const totals = data?.totals || null;
  const nothingYet = totals && !totals.xray_tests && !totals.other_testcases;
  const xrayErrors = (data?.tickets || []).filter((t) => t.xray_error);

  return (
    <section
      className={`rounded-2xl border border-slate-200 bg-card p-4 shadow-sm ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-[10px] font-bold text-white">
            <Icons.Copy className="h-3 w-3" />
          </span>
          {title}
          {keys.length > 0 && (
            <span className="font-mono text-[11px] font-medium text-slate-400">
              {keys.join(", ")}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <Icons.Refresh className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            Re-check
          </button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100"
          >
            {open ? "Hide" : "Show"}
            <Icons.ChevronDown
              className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {loading && !data && (
        <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
          <Icons.Spinner className="h-3.5 w-3.5 animate-spin" />
          Checking Jira and this test suite for tests that already exist…
        </p>
      )}

      {error && (
        <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error} — duplicates can't be detected right now, so check manually before
          you create.
        </p>
      )}

      {totals && (
        <div
          className={`mt-2 rounded-lg border px-3 py-2 text-xs ${
            nothingYet
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
        >
          <p className="flex items-start gap-1.5 font-medium">
            {nothingYet ? (
              <Icons.Check className="mt-0.5 h-3.5 w-3.5 flex-none" />
            ) : (
              <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" />
            )}
            <span>{coverageSummaryLine(totals)}</span>
          </p>
          {!nothingYet && (
            <p className="mt-1 pl-5 text-[11px] leading-snug">
              Anything listed below is treated as done: Cortex won't create it a
              second time. Only the missing coverage gets built.
            </p>
          )}
          {totals.drifted > 0 && (
            <p className="mt-1 pl-5 text-[11px] leading-snug">
              {totals.drifted} automated test
              {totals.drifted === 1 ? " has" : "s have"} drifted — the test steps
              changed after the Artifact was authored. Re-import those on
              purpose to refresh them.
            </p>
          )}
        </div>
      )}

      {xrayErrors.length > 0 && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          Couldn't read the tests for {xrayErrors.map((t) => t.ticket_key).join(", ")}.
          Existing tests may not be listed.
        </p>
      )}

      {open && data && (
        <div className="mt-3 space-y-4">
          {(data.tickets || []).map((t) => (
            <div key={t.ticket_key}>
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] font-semibold text-brand-700">
                  {t.ticket_key}
                </span>
                <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">
                  {t.summary}
                </span>
                <Pill tone={t.total_xray ? "violet" : "slate"}>
                  {t.total_xray} test{t.total_xray === 1 ? "" : "s"}
                </Pill>
                <Pill tone={t.automated_count ? "green" : "slate"}>
                  {t.automated_count} automated
                </Pill>
                {t.not_automated_count > 0 && (
                  <Pill tone="amber">{t.not_automated_count} to automate</Pill>
                )}
              </div>

              {(t.test_set_keys?.length > 0 || t.test_plan_keys?.length > 0) && (
                <p className="mb-1 text-[10px] text-slate-400">
                  {t.test_set_keys?.length > 0 && (
                    <>Test Sets: {t.test_set_keys.join(", ")}. </>
                  )}
                  {t.test_plan_keys?.length > 0 && (
                    <>Test Plans: {t.test_plan_keys.join(", ")}.</>
                  )}
                </p>
              )}

              {t.tests?.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full table-fixed text-[11px]">
                    <caption className="sr-only">
                      Tests already linked to {t.ticket_key} and their
                      Artifact status
                    </caption>
                    <thead className="bg-slate-50 text-left text-slate-500">
                      <tr>
                        <th scope="col" className="w-24 px-2 py-1.5 font-semibold">
                          Test
                        </th>
                        <th scope="col" className="px-2 py-1.5 font-semibold">
                          Summary
                        </th>
                        <th scope="col" className="w-14 px-2 py-1.5 font-semibold">
                          Steps
                        </th>
                        <th scope="col" className="w-48 px-2 py-1.5 font-semibold">
                          Artifact
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.tests.map((x) => {
                        const isOpen = expanded.has(`${t.ticket_key}:${x.key}`);
                        return (
                          <React.Fragment key={x.key}>
                            <tr className="border-t border-slate-100 align-top">
                              <td className="px-2 py-1.5 font-mono font-semibold text-violet-700">
                                {x.key}
                              </td>
                              <td className="px-2 py-1.5 text-slate-700">
                                {x.summary || "(no summary)"}
                              </td>
                              <td className="px-2 py-1.5 text-slate-500">
                                {x.steps_count}
                              </td>
                              <td className="px-2 py-1.5">
                                {x.automated ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleRow(`${t.ticket_key}:${x.key}`)}
                                    className="flex flex-wrap items-center gap-1 text-left"
                                  >
                                    <Pill tone="green">
                                      <Icons.Check className="h-2.5 w-2.5" />
                                      Automated ({x.testcases.length})
                                    </Pill>
                                    {x.drifted && <Pill tone="amber">steps changed</Pill>}
                                    <Icons.ChevronDown
                                      className={`h-3 w-3 text-slate-400 transition-transform ${
                                        isOpen ? "rotate-180" : ""
                                      }`}
                                    />
                                  </button>
                                ) : (
                                  <Pill tone="slate">Not automated yet</Pill>
                                )}
                              </td>
                            </tr>
                            {isOpen && x.automated && (
                              <tr className="border-t border-slate-100 bg-slate-50/60">
                                <td colSpan={4} className="px-2 py-1.5">
                                  <ul className="space-y-1">
                                    {x.testcases.map((c) => (
                                      <li
                                        key={c.testcasekey}
                                        className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600"
                                      >
                                        <Icons.ChevronRight className="h-3 w-3 flex-none text-slate-300" />
                                        <span className="font-medium text-slate-800">
                                          {c.name}
                                        </span>
                                        <Pill tone="slate">{c.board_name}</Pill>
                                        <Pill
                                          tone={
                                            c.status === "passed"
                                              ? "green"
                                              : c.status === "failed"
                                                ? "rose"
                                                : "slate"
                                          }
                                        >
                                          {c.status}
                                        </Pill>
                                        <span className="text-slate-400">
                                          {c.steps_count} step
                                          {c.steps_count === 1 ? "" : "s"}
                                        </span>
                                        {c.drifted && (
                                          <Pill tone="amber">Test steps changed since</Pill>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                !t.xray_error && (
                  <p className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-500">
                    No tests are linked to {t.ticket_key} yet.
                  </p>
                )
              )}

              {t.other_testcases?.length > 0 && (
                <div className="mt-2">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Other Cortex test cases on this ticket (not from a Jira test)
                  </p>
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <table className="w-full table-fixed text-[11px]">
                      <thead className="bg-slate-50 text-left text-slate-500">
                        <tr>
                          <th scope="col" className="px-2 py-1.5 font-semibold">
                            Test case
                          </th>
                          <th scope="col" className="w-32 px-2 py-1.5 font-semibold">
                            Test Suite
                          </th>
                          <th scope="col" className="w-20 px-2 py-1.5 font-semibold">
                            Steps
                          </th>
                          <th scope="col" className="w-24 px-2 py-1.5 font-semibold">
                            Created by
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.other_testcases.map((c) => (
                          <tr key={c.testcasekey} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 text-slate-700">{c.name}</td>
                            <td className="px-2 py-1.5 text-slate-500">
                              {c.board_name}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500">
                              {c.steps_count}
                            </td>
                            <td className="px-2 py-1.5 text-slate-500">
                              {c.creation === "ai" ? "AI" : "Manual"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
